/* eslint-disable no-nested-ternary */
/* eslint-disable react/prop-types */
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Typography, Chip, Divider, Box, IconButton, Tooltip, Grid, Avatar } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Iconify } from 'src/components';
import { format } from 'date-fns';
import { campaignActionApproval } from 'src/utils/campaignTemplateApproval';

function Row({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 120 }}>{label}:</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
    </Stack>
  );
}

export default function CampaignEventDialog({ open, onClose, campaign, onPreview, onAudience, onDelete, onPauseResume }) {
  const theme = useTheme();
  const resumeApproval = campaignActionApproval(campaign);

  const f = (s) => {
    if (!s) return '-';
    try {
      // s comes like 02-07-2025 13:32:31.309816Z (DD-MM-YYYY)
      // eslint-disable-next-line no-unused-vars
      const [d, t] = String(s).split(' ');
      const [dd, mm, yyyy] = d.split('-').map((n) => parseInt(n, 10));
      const dt = new Date(Date.UTC(yyyy, (mm || 1) - 1, dd || 1, 0, 0, 0));
      return format(dt, 'd MMM yyyy');
    } catch { return s; }
  };

  const recipients = campaign?.audience?.audience?.recepient_count;
  const audienceName = campaign?.audience?.audience?.name;

  const chColor = (ch) => {
    const c = String(ch || '').toUpperCase();
    switch (c) {
      case 'WHATSAPP': return theme.palette.success.main;
      case 'EMAIL': return theme.palette.info.main;
      case 'SMS': return theme.palette.error.main;
      default: return theme.palette.text.secondary;
    }
  };

  const channelIcon = (ch) => {
    const c = String(ch || '').toUpperCase();
    switch (c) {
      case 'WHATSAPP': return 'ic:baseline-whatsapp';
      case 'EMAIL': return 'mdi:email-outline';
      case 'SMS': return 'material-symbols:sms';
      default: return 'mdi:label-outline';
    }
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose} disableScrollLock>
      <DialogTitle>
        Campaign details
        {!!campaign && (
          <Box sx={{ float: 'right' }}>
            <Tooltip title="Preview"><IconButton size="small" onClick={() => onPreview?.(campaign)}><Iconify icon="mdi:eye-outline" /></IconButton></Tooltip>
            <Tooltip title="Audience"><IconButton size="small" onClick={() => onAudience?.(campaign)}><Iconify icon="mdi:account-group-outline" /></IconButton></Tooltip>
            {String(campaign?.status || '').toUpperCase() === 'SCHEDULED' && (
              <Tooltip title="Pause">
                <IconButton size="small" onClick={() => onPauseResume?.(campaign)}>
                  <Iconify icon="mdi:pause-circle-outline" />
                </IconButton>
              </Tooltip>
            )}
            {String(campaign?.status || '').toUpperCase() === 'PAUSED' && (
              <Tooltip title={resumeApproval.allowed ? 'Resume' : resumeApproval.reason}>
                <span>
                  <IconButton
                    aria-label="Resume"
                    size="small"
                    disabled={!resumeApproval.allowed}
                    onClick={() => onPauseResume?.(campaign)}
                  >
                    <Iconify icon="mdi:play-circle-outline" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => onDelete?.(campaign)}><Iconify icon="mdi:trash-can-outline" /></IconButton></Tooltip>
          </Box>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {campaign ? (
          <Stack spacing={2}>
            {/* Header block with avatar + name + badges */}
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: chColor(campaign?.channel), width: 36, height: 36 }}>
                <Iconify icon={channelIcon(campaign?.channel)} width={20} height={20} />
              </Avatar>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="h6" noWrap>{campaign?.name || 'Untitled campaign'}</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                  <Chip size="small" variant="outlined" label={String(campaign?.channel || '').toUpperCase()} sx={{ borderColor: chColor(campaign?.channel), color: chColor(campaign?.channel) }} />
                  <Chip size="small" label={String(campaign?.status || '').toUpperCase()} color={(String(campaign?.status || '').toUpperCase() === 'SENT') ? 'success' : (String(campaign?.status || '').toUpperCase() === 'SCHEDULED') ? 'info' : (String(campaign?.status || '').toUpperCase() === 'PAUSED') ? 'warning' : (String(campaign?.status || '').toUpperCase() === 'CANCELLED') ? 'error' : 'default'} variant="soft" />
                </Stack>
              </Box>
              <Typography variant="caption" color="text.secondary">ID: {campaign?.id}</Typography>
            </Stack>

            <Divider />

            {/* Details grid */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Stack spacing={1.25}>
                  <Row label="Created" value={f(campaign?.created_at)} />
                  <Row label="Scheduled" value={f(campaign?.scheduled_at)} />
                  <Row label="Processed" value={campaign?.result?.successfully_processed_count ?? '-'} />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={1.25}>
                  <Row label="Audience" value={audienceName} />
                  <Row label="Recipients" value={typeof recipients === 'number' ? recipients : '-'} />
                  {!!audienceName && (
                    <Box>
                      <Button size="small" variant="outlined" startIcon={<Iconify icon="mdi:account-group-outline" />} onClick={() => onAudience?.(campaign)}>
                        View audience
                      </Button>
                    </Box>
                  )}
                </Stack>
              </Grid>
            </Grid>

            {!!campaign?.tags?.length && (
              <>
                <Divider />
                <Typography variant="subtitle2">Tags</Typography>
                <Box sx={{ mt: 0.5 }}>
                  {campaign.tags.map((t, i) => (
                    <Chip key={i} size="small" label={t} sx={{ mr: 0.5, mb: 0.5 }} />
                  ))}
                </Box>
              </>
            )}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">No campaign selected</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

CampaignEventDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  campaign: PropTypes.object,
  onPreview: PropTypes.func,
  onAudience: PropTypes.func,
  onDelete: PropTypes.func,
  onPauseResume: PropTypes.func,
};
